import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Agent, Project, ProjectAgent, User, UserKey, ProjectKey, RitzAPI, AgentLoginInfo } from '@moderepo/mode-internal-apis';
import { fetchSudoUserAccessKey, selectSudoUserAccessKey, selectSudoProjectAccessKey, ExtDispatch, setSudoProjectAccessKey, BaseAction,
    authenticateSuccess, setProjectAgent, setUser, setSudoUserAccessKey, batchActions, setProject, RitzDataStateAction, RitzDataState,
    selectAgentAuth } from '@moderepo/mode-data-state-ritz';
import { AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS, deleteLocalStorageItem, Time } from '@moderepo/mode-app-components';
import { AppAPI } from '@moderepo/mode-apis';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { LS_KEY_LOGIN_INFO } from '../../utils/constants';



const loadAgent = async (loginInfo: AgentLoginInfo | undefined, loggedInUser: Agent | undefined): Promise<Agent | undefined> => {

    if (loginInfo && loginInfo.authenticated) {

        if (loggedInUser) {
            // If we have both loginInfo and agent profile data then return the agent profile data
            return loggedInUser;
        }

        try {
            // If we have loginInfo but don't have agent profile data, try to load agent profile
            return await RitzAPI.getInstance().getAgentProfile(loginInfo.agentId);
        } catch (error) {
            // LoginInfo we have is probably expired. Delete login info from local storage to make sure the user go directly to
            // the login page next time
            deleteLocalStorageItem(LS_KEY_LOGIN_INFO);
        }
    }

    return undefined;
};


const loadProject = async (
    agentId: number | undefined, selectedProjectId: number | undefined, cachedProject: Project | undefined,
    cachedProjectAgent: ProjectAgent | undefined, cachedProjectKey: ProjectKey | undefined,
): Promise<{
    selectedProject: Project | undefined,
    selectedProjectAgent: ProjectAgent | undefined,
    selectedProjectKey: ProjectKey | undefined,
}> => {


    if (agentId && selectedProjectId) {
        try {
            const selectedProject = cachedProject && cachedProject.id === selectedProjectId
                ? cachedProject
                : await RitzAPI.getInstance().getAgentProjectById(agentId, selectedProjectId);

            // If the agent has access to the selectedProject then continue loading projectAgent data
            if (selectedProject) {
                // Load project agent data if we don't have it yet or if the currentProjectAgent DOES NOT match the logged in agentId
                const selectedProjectAgent = cachedProjectAgent && cachedProjectAgent.agentId === agentId
                    ? cachedProjectAgent
                    : await RitzAPI.getInstance().getProjectAgent(selectedProjectId, agentId);

                const selectedProjectKey = cachedProjectKey
                    && (!cachedProjectKey.expirationTime || Date.parse(cachedProjectKey.expirationTime) > Date.now())
                    ? cachedProjectKey
                    : await RitzAPI.getInstance().generateProjectKey(selectedProjectId);

                return {
                    selectedProject,
                    selectedProjectAgent,
                    selectedProjectKey,
                };
            }

            return {
                selectedProject     : undefined,
                selectedProjectAgent: undefined,
                selectedProjectKey  : undefined,
            };
        } catch (error) {
            // don't need to do anything, just return undefined for everything
        }
    }

    return {
        selectedProject     : undefined,
        selectedProjectAgent: undefined,
        selectedProjectKey  : undefined,
    };
};


const loadUser = async (
    projectId: number | undefined, userId: number | undefined, cachedUser: User | undefined, cachedUserKey: UserKey | undefined,
): Promise<{
    user: User | undefined,
    userKey: UserKey | undefined
}> => {
    if (projectId && userId) {
        try {
            // Load user profile if we don't have it yet or if the current user profile belonged
            // to a different user
            const user = cachedUser && cachedUser.id === userId
                ? cachedUser
                : await RitzAPI.getInstance().getUserById(projectId, userId);

            const userKey = cachedUserKey
            && (!cachedUserKey.expirationTime || Date.parse(cachedUserKey.expirationTime) > Date.now())
                ? cachedUserKey
                : await await RitzAPI.getInstance().createUserKey(projectId, userId);

            return {
                user,
                userKey,
            };
        } catch (error) {
            // don't need to do anything, just continue and return undefined for everything
        }
    }

    return {
        user   : undefined,
        userKey: undefined,
    };
};


export interface RouteRequiredData {
    readonly agent: Agent | undefined,
    readonly selectedProject: Project | undefined,
    readonly selectedProjectAgent: ProjectAgent | undefined,
    readonly selectedProjectKey: ProjectKey | undefined,
    readonly user: User | undefined,
    readonly userKey: UserKey | undefined,
}


export const loadRequiredData = async (
    currentProjectId: number | undefined,
    userId: number | undefined,
    dataDispatch: ExtDispatch<RitzDataStateAction>,
    loginInfo: AgentLoginInfo | undefined,
    loggedInUser: Agent | undefined,
    cachedProject: Project | undefined,
    cachedProjectAgent: ProjectAgent | undefined,
    cachedProjectKey: ProjectKey | undefined,
    cachedUserKey: UserKey | undefined,
    cachedUser: User | undefined,
): Promise<RouteRequiredData | undefined> => {

    const loadData = async () => {

        const result: RouteRequiredData = {
            agent: await loadAgent(loginInfo, loggedInUser),
            ...await loadProject(
                loginInfo?.agentId, currentProjectId, cachedProject, cachedProjectAgent, cachedProjectKey,
            ),
            ...await loadUser(currentProjectId, userId, cachedUser, cachedUserKey),
        };


        const actions: BaseAction[] = [];

        if (result.agent) {
            actions.push(authenticateSuccess(result.agent));
        }
        if (result.selectedProject) {
            actions.push(setProject(result.selectedProject));
        }
        if (currentProjectId && result.selectedProjectAgent) {
            actions.push(setProjectAgent(currentProjectId, result.selectedProjectAgent));

            if (result.selectedProjectKey) {
                actions.push(setSudoProjectAccessKey(currentProjectId, result.selectedProjectKey));

                // AppAPI needs project keys to make API call to TSDB without user context
                AppAPI.getInstance().setAuthToken(result.selectedProjectKey.token);

                // App Proxy API also need project key to make API call to app proxy server. We will find the projectTypeHandler for the
                // current selected project and if there is one, get the appProxyHandler for that project handler and provide it the project key
                const projectTypeHandler = ProjectTypeHandlersManager.getInstance().getAppProxyHandler(
                    result.selectedProject?.supportToolSettings.projectType,
                );
                if (projectTypeHandler) {
                    const appProxyAPI = projectTypeHandler.getAppProxyAPI();
                    if (appProxyAPI) {
                        appProxyAPI.setProjectToken(result.selectedProjectKey.token);
                    }
                }
            }
        }
        if (currentProjectId && result.user) {
            actions.push(setUser(currentProjectId, result.user));
            if (result.user && result.userKey) {
                actions.push(setSudoUserAccessKey(result.user.id, result.userKey));

                // AppAPI needs project key or user key to make API call to TSDB. If we have userKey, use userKey instead of projectKey
                AppAPI.getInstance().setAuthToken(result.userKey.userSessionToken);
            }
        } else if (!result.user) {
            // If there is no user context in the route, we need to clear the token that was previously set for the AppAPI. That token
            // should not be used anymore
            AppAPI.getInstance().clearAuthToken();

            // If we have project key, then set change the AppAPI token to the project key so that all the API call through AppAPI going
            // forward will be using the project key
            if (result.selectedProjectKey) {
                AppAPI.getInstance().setAuthToken(result.selectedProjectKey.token);
            }
        }

        dataDispatch(batchActions(actions));
        return result;
    };


    return loadData();
};



/**
 * This customHook is used for fetching and monitoring projectKey. It will fetch and return a project key for the specified projectId AND also
 * keep monitoring the project key's expiration time and will automatically renew the key when it is expired.
 * @param dataState
 * @param dataDispatch
 * @param projectId
 */
export const useFetchAndMonitorProjectKey = (
    dataState: RitzDataState,
    dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number,
): ProjectKey | null | undefined => {
    const cachedProjectKey = selectSudoProjectAccessKey(dataState, projectId);
    const [projectKey, setProjectKey] = useState<ProjectKey | null | undefined>(
        cachedProjectKey && Date.parse(cachedProjectKey.expirationTime) >= Date.now() ? cachedProjectKey : undefined,
    );
    const auth = selectAgentAuth(dataState);
    const timer = useRef<number | undefined>();
    const componentUnmounted = useRef<boolean>(false);


    const fetchProjectKey = useCallback(async () => {
        try {
            if (auth.loggedInAgent) {
                const key = await RitzAPI.getInstance().generateProjectKey(projectId);
                if (!componentUnmounted.current) {
                    dataDispatch(setSudoProjectAccessKey(projectId, key));
                    setProjectKey(key);
                }
            } else if (!componentUnmounted.current) {
                setProjectKey(null);
            }
        } catch (error) {
            if (!componentUnmounted.current) {
                setProjectKey(null);
            }
        }
    }, [auth.loggedInAgent, dataDispatch, projectId]);


    // Fetch the key at least once when the hook is created
    useEffect(() => {
        if (!projectKey) {
            fetchProjectKey();
        }
    }, [projectKey, fetchProjectKey]);


    // Create an interval to keep checking if the key is expired and re-fetch new key when it is expired
    useEffect(() => {
        componentUnmounted.current = false;
        if (projectKey) {
            // Create an interval to keep checking if the key is expired.
            timer.current = window.setInterval(() => {
                // If key is expired or will expire before the next check, re-fetch a new key
                if (Date.parse(projectKey.expirationTime) <= Date.now() + AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS + Time.MINUTE_IN_MS) {
                    fetchProjectKey();
                }
            }, AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS);
        }

        // Return a cleanup function that will clear the timer when component is unmounted
        return () => {
            componentUnmounted.current = true;
            if (timer.current) {
                window.clearInterval(timer.current);
                timer.current = undefined;
            }
        };
    }, [projectKey, fetchProjectKey, projectId]);


    return projectKey;
};



/**
 * This customHook is used for fetching and monitoring userKey. It will fetch and return a user key for the specified projectId and userId AND also
 * keep monitoring the user key's expiration time and will automatically renew the key when it is expired.
 * @param dataState
 * @param dataDispatch
 * @param projectId
 */
export const useFetchAndMonitorUserKey = (
    dataState: RitzDataState,
    dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number,
    userId: number | undefined,
): UserKey | null | undefined => {

    const userKey = useMemo(() => {
        const cachedUserKey = userId ? selectSudoUserAccessKey(dataState, userId) : undefined;
        if (cachedUserKey && cachedUserKey.userId === userId && Date.parse(cachedUserKey.expirationTime) >= Date.now()) {
            return cachedUserKey;
        }
        return undefined;
    }, [dataState, userId]);

    const timer = useRef<number | undefined>();

    const fetchUserKey = useCallback(async () => {
        if (userId) {
            try {
                await dataDispatch(fetchSudoUserAccessKey(projectId, userId));
            } catch (error) {
            // ignore error
            }
        }
    }, [dataDispatch, projectId, userId]);


    // Fetch the key at least once when the hook is created
    useEffect(() => {
        if (!userKey || userKey.userId !== userId) {
            fetchUserKey();
        }
    }, [userKey, fetchUserKey, userId]);



    // Create an interval to keep checking if the key is expired and re-fetch new key when it is expired
    useEffect(() => {
        if (userKey) {
            timer.current = window.setInterval(() => {
                // If key is expired or will expire before the next check, re-fetch a new key
                if (Date.parse(userKey.expirationTime) <= Date.now() + AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS + Time.MINUTE_IN_MS) {
                    fetchUserKey();
                }
            }, AUTH_KEY_EXPIRATION_CHECK_FREQ_IN_MS);
        }

        // Return a cleanup function that will clear the timer when component is unmounted
        return () => {
            if (timer.current) {
                window.clearInterval(timer.current);
                timer.current = undefined;
            }
        };
    }, [userKey, fetchUserKey, userId]);

    return userKey;
};
